import { useState, useEffect } from "react";
import {
	TextField,
	Button,
	Typography,
	Box,
	Alert,
	LinearProgress,
	Grid,
	IconButton,
	Divider,
	Tooltip,
	Autocomplete,
	Tabs,
	Tab,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import RefreshIcon from "@mui/icons-material/Refresh";
import HeaderReport from "../../components/header/report";
import { getStatus, fetchTemplates, sendEvolutionTemplate } from "./req/reqOfficial";
import Footer from "../../components/footer";
import reqEmpresaCredenciais from "../../services/reqMultiTenant/reqEmpresaCredenciais";
import reqWhatsapp from "../../services/reqMultiTenant/reqWhatsApp";
import { toast, ToastContainer } from "react-toastify";
import UploadPhotoV2Image from "../../components/uploading/uploadingPhotoV2image";
import MainRegisterWhatsApp from "../register/whatsapp/main";

const WhatsAppSenderOfficial = () => {
	const [activeTab, setActiveTab] = useState(0);

	// Estado para configuração do Template
	const [templateName, setTemplateName] = useState("");
	const [templateLang, setTemplateLang] = useState("pt_BR");
	const [availableTemplates, setAvailableTemplates] = useState([]);

	// Variáveis do template: lista de objetos { id, value }
	const [templateVars, setTemplateVars] = useState([]);
	const [imagePreview, setImagePreview] = useState(null);

	const [phoneNumber, setPhoneNumber] = useState("");

	const [status, setStatus] = useState("");
	const [loading, setLoading] = useState(true);
	const [isSending, setIsSending] = useState(false);

	const [instanciaWhatsapp, setInstanciaWhatsapp] = useState([]);
	const [credentials, setCredentials] = useState([]);
	const [selectedInstance, setSelectedInstance] = useState(null);

	useEffect(() => {
		fetchCredentials();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	useEffect(() => {
		if (instanciaWhatsapp.length > 0 && !selectedInstance) {
			const firstWithPhone = instanciaWhatsapp.find((instance) => instance?.telefone);
			setSelectedInstance(firstWithPhone || instanciaWhatsapp[0]);
		}
	}, [instanciaWhatsapp, selectedInstance]);

	useEffect(() => {
		if (selectedInstance && credentials) {
			loadTemplates();
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedInstance, credentials]);

	const loadTemplates = async (manual = false) => {
		try {
			if (!selectedInstance) {
				if (manual) toast.warning("Selecione uma instância primeiro.");
				return;
			}

			const cred = await getCredentialsForInstance(selectedInstance);
			const templates = await fetchTemplates(cred.url, cred.apiKey, cred.instance);

			const list = Array.isArray(templates) ? templates : templates?.data || [];
			setAvailableTemplates(list);
			if (manual) toast.success("Templates atualizados!");
		} catch (error) {
			console.error("Erro ao carregar templates:", error);
			if (manual) toast.error("Erro ao buscar templates.");
		}
	};

	// =========================
	// Helpers
	// =========================

	const getCredObject = () => {
		if (!credentials) return null;
		if (Array.isArray(credentials)) return credentials[0] || null;
		return credentials;
	};

	const normalize = (v) =>
		String(v ?? "")
			.trim()
			.toUpperCase();

	const isConnectedStatus = (st) => {
		const raw = st?.status ?? st?.state ?? st?.instance?.status ?? st?.instance?.state;
		const s = normalize(raw);
		const CONNECTED_VALUES = new Set([
			"CONNECTED",
			"CONNECTION_OPEN",
			"OPEN",
			"ONLINE",
			"READY",
			"ACTIVE",
			"TRUE",
			"1",
		]);
		const DISCONNECTED_VALUES = new Set([
			"DISCONNECTED",
			"CLOSED",
			"OFFLINE",
			"NOT_FOUND",
			"FALSE",
			"0",
			"UNPAIRED",
		]);

		const boolConnected = st?.connected === true || st?.isConnected === true;
		if (boolConnected) return true;
		if (!s) return false;
		if (DISCONNECTED_VALUES.has(s)) return false;
		if (CONNECTED_VALUES.has(s)) return true;
		return true;
	};

	const getCredentialsForInstance = async (instanceObj) => {
		try {
			const credObj = getCredObject();
			const apiKey = credObj?.api_key;
			const url = credObj?.url;
			const instance = instanceObj?.descricao;

			if (!apiKey || !url) throw new Error("Credenciais não carregadas.");
			if (!instance) throw new Error("Nenhuma instância definida.");

			const statusData = await getStatus(url, apiKey, instance);
			if (!isConnectedStatus(statusData)) {
				throw new Error(`A instância "${instance}" não está conectada.`);
			}

			return { apiKey, url, instance };
		} catch (error) {
			console.error("Erro ao obter credenciais:", error);
			throw error;
		}
	};

	const handleOpenUploadComponent = (e) => {
		e.preventDefault();
		const event = new CustomEvent("upload-whatsapp");
		window.dispatchEvent(event);
	};

	// =========================
	// Data fetch
	// =========================

	const fetchCredentials = async () => {
		try {
			setLoading(true);
			const [empresaCredenciaisRes, whatsappRes] = await Promise.all([
				reqEmpresaCredenciais.listId("whatsapp"),
				reqWhatsapp.listPaginate("descricao", "", "SIM", 1),
			]);
			setCredentials(empresaCredenciaisRes.data);
			setInstanciaWhatsapp(whatsappRes.data);
		} catch (error) {
			toast.warning(error?.message || "Erro ao buscar credenciais");
		} finally {
			setLoading(false);
		}
	};

	// =========================
	// Template Config Logic
	// =========================

	const addVariable = () => {
		setTemplateVars([...templateVars, { id: Date.now(), value: "" }]);
	};

	const removeVariable = (index) => {
		const updated = [...templateVars];
		updated.splice(index, 1);
		setTemplateVars(updated);
	};

	const updateVariable = (index, newValue) => {
		const updated = [...templateVars];
		updated[index].value = newValue;
		setTemplateVars(updated);
	};

	// =========================
	// Sending Logic (TEMPLATE)
	// =========================

	const sendMessage = async () => {
		try {
			setIsSending(true);

			if (!templateName.trim()) {
				toast.warning("Por favor, defina o nome do template.");
				return;
			}

			if (!phoneNumber.trim()) {
				toast.warning("Por favor, informe o número do destinatário.");
				return;
			}

			const credObj = getCredObject();
			if (!credObj?.api_key || !credObj?.url) {
				toast.error("Credenciais não carregadas.");
				return;
			}

			if (!selectedInstance) {
				toast.warning("Selecione uma instância.");
				return;
			}

			setStatus("🚀 Enviando (API Oficial)...");

			const cred = await getCredentialsForInstance(selectedInstance);
			const variablesToSend = templateVars.map((v) => v.value || "");

			await sendEvolutionTemplate(
				cred.url,
				cred.apiKey,
				cred.instance,
				phoneNumber,
				templateName,
				templateLang,
				variablesToSend,
				imagePreview,
			);

			setStatus(`✅ Template enviado para ${phoneNumber} via ${cred.instance}.`);
			toast.success("Mensagem enviada com sucesso!");
		} catch (error) {
			console.error("Erro envio:", error);
			const errorMsg = error?.response?.data?.message || error?.message || "Erro desconhecido";
			toast.error(typeof errorMsg === "object" ? JSON.stringify(errorMsg) : String(errorMsg));
			setStatus(`❌ Erro: ${errorMsg}`);
		} finally {
			setIsSending(false);
		}
	};

	// =========================
	// UI
	// =========================

	return (
		<>
			<ToastContainer />
			<div className="mt-page">
				<Box maxWidth="lg" mx="auto" p={2}>
					<HeaderReport title={"WhatsApp Oficial (API Cloud)"} subTitle={"Envio individual via Template"} />

					<Tabs
						value={activeTab}
						onChange={(e, newValue) => setActiveTab(newValue)}
						sx={{ mb: 3 }}
						indicatorColor="primary"
						textColor="primary"
					>
						<Tab label="Envio de Mensagem" />
						<Tab label="Cadastro de Instância" />
					</Tabs>

					{activeTab === 1 ? (
						<MainRegisterWhatsApp />
					) : (
						<>
					{/* Instância e Destinatário */}
					<Grid container spacing={3} mb={3}>
						<Grid item xs={12}>
							<Box className="border p-4 rounded shadow-sm bg-white">
								<Typography variant="h6" gutterBottom color="primary">
									1. Configuração de Instância
								</Typography>
								<Box mb={2}>
									<select
										value={selectedInstance ? JSON.stringify(selectedInstance) : ""}
										onChange={(e) =>
											setSelectedInstance(e.target.value ? JSON.parse(e.target.value) : null)
										}
										className="border rounded p-2 w-full"
										disabled={isSending}
									>
										<option value="">Selecione a instância</option>
										{instanciaWhatsapp.filter(Boolean).map((instance, idx) => (
											<option key={idx} value={JSON.stringify(instance)}>
												{instance.descricao} - {instance.telefone || "Sem número"}
											</option>
										))}
									</select>
									{!selectedInstance && (
										<Alert severity="warning" sx={{ mt: 1 }}>
											Selecione uma instância conectada para continuar.
										</Alert>
									)}
									{selectedInstance && (
										<Alert severity="success" sx={{ mt: 1 }}>
											Instância selecionada: {selectedInstance.descricao}
										</Alert>
									)}
								</Box>

								<Divider sx={{ my: 2 }} />

								<Typography variant="h6" gutterBottom color="primary">
									2. Destinatário
								</Typography>
								<TextField
									label="Número do destinatário"
									placeholder="Ex: 5511999999999"
									fullWidth
									value={phoneNumber}
									onChange={(e) => setPhoneNumber(e.target.value)}
									disabled={isSending}
									size="small"
									error={!phoneNumber}
									helperText="Informe DDI + DDD + número"
								/>
							</Box>
						</Grid>

						<Grid item xs={12}>
							<Box className="border p-4 rounded shadow-sm bg-white">
								<Typography variant="h6" gutterBottom color="primary">
									3. Configuração do Template
								</Typography>

								<Alert severity="info" sx={{ mb: 2, fontSize: "0.85rem" }}>
									<strong>Instruções:</strong>
									<br />
									1. Insira o <strong>Nome do Template</strong> (exatamente como no Gerenciador da
									Meta).
									<br />
									2. Se o template tiver variáveis (ex: <code>Olá {"{{1}}"}</code>), clique em{" "}
									<strong>Adicionar</strong> e informe o valor.
									<br />
									3. A ordem das variáveis deve ser a mesma do texto (<code>{"{{1}}"}</code> = 1ª
									variável, etc).
								</Alert>

								{(!templateName || templateName.trim() === "") && (
									<Alert severity="warning" sx={{ mb: 2 }}>
										⚠️ O nome do template é obrigatório.
									</Alert>
								)}

								<Box mb={2} p={2} bgcolor="#f8f9fa" borderRadius={2} border="1px dashed #ced4da">
									<Typography variant="subtitle2" gutterBottom>
										Mídia do Cabeçalho (Opcional)
									</Typography>
									<Box display="flex" alignItems="center" gap={2}>
										{imagePreview ? (
											<Box
												component="img"
												src={imagePreview}
												alt="Preview"
												sx={{
													width: 80,
													height: 80,
													objectFit: "cover",
													borderRadius: 1,
													border: "1px solid #ddd",
												}}
											/>
										) : (
											<Box
												sx={{
													width: 80,
													height: 80,
													bgcolor: "#eee",
													display: "flex",
													alignItems: "center",
													justifyContent: "center",
													borderRadius: 1,
													color: "#999",
													fontSize: "0.75rem",
													textAlign: "center",
													p: 1,
												}}
											>
												Sem Imagem
											</Box>
										)}
										<Box flex={1}>
											<Button
												variant="contained"
												color={imagePreview ? "warning" : "primary"}
												onClick={handleOpenUploadComponent}
												disabled={isSending}
												size="small"
												startIcon={imagePreview ? <DeleteIcon /> : <AddIcon />}
											>
												{imagePreview ? "Trocar Imagem" : "Enviar Imagem"}
											</Button>
											{imagePreview && (
												<Button
													variant="text"
													color="error"
													size="small"
													onClick={() => setImagePreview(null)}
													sx={{ ml: 1 }}
													disabled={isSending}
												>
													Remover
												</Button>
											)}
										</Box>
									</Box>
								</Box>

								<Grid container spacing={2}>
									<Grid item xs={8}>
										<Box display="flex" gap={1} alignItems="flex-start">
											<Autocomplete
												options={availableTemplates.map((t) => t.name)}
												freeSolo
												value={templateName}
												onChange={(event, newValue) => {
													setTemplateName(newValue);
													// Tenta encontrar o idioma do template selecionado
													const found = availableTemplates.find((t) => t.name === newValue);
													if (found && found.language) {
														setTemplateLang(found.language);
													}
												}}
												onInputChange={(event, newInputValue) => {
													setTemplateName(newInputValue);
												}}
												renderInput={(params) => (
													<TextField
														{...params}
														label="Nome do Template"
														helperText={
															availableTemplates.length > 0
																? "Selecione ou digite o nome"
																: "Digite o nome exato (aprovado na Meta)"
														}
														error={!templateName}
														size="small"
													/>
												)}
												disabled={isSending}
												fullWidth
											/>
											<Tooltip title="Recarregar Templates">
												<span>
													<IconButton
														onClick={() => loadTemplates(true)}
														disabled={isSending || loading}
														color="primary"
														size="small"
														sx={{ mt: 0.5 }}
													>
														<RefreshIcon />
													</IconButton>
												</span>
											</Tooltip>
										</Box>
									</Grid>
									<Grid item xs={4}>
										<TextField
											label="Idioma"
											fullWidth
											value={templateLang}
											onChange={(e) => setTemplateLang(e.target.value)}
											size="small"
											disabled={isSending}
											helperText="Ex: pt_BR"
										/>
									</Grid>
								</Grid>

								<Box mt={3}>
									<Box display="flex" justifyContent="space-between" alignItems="center">
										<Typography variant="subtitle1" fontWeight="bold">
											Variáveis do Template
										</Typography>
										<Button
											startIcon={<AddIcon />}
											size="small"
											onClick={addVariable}
											disabled={isSending}
											variant="outlined"
										>
											Adicionar Variável
										</Button>
									</Box>

									<Alert severity="info" sx={{ mt: 1, py: 0, fontSize: "0.80rem" }}>
										Adicione as variáveis na ordem do template (<code>{"{{1}}"}</code>,{" "}
										<code>{"{{2}}"}</code>...) e informe o valor a ser enviado.
									</Alert>

									{templateVars.length === 0 && (
										<Typography
											variant="caption"
											display="block"
											color="textSecondary"
											sx={{ mt: 2, fontStyle: "italic" }}
										>
											Nenhuma variável adicionada. Se o template não tem variáveis, deixe vazio.
										</Typography>
									)}

									<Box sx={{ maxHeight: 200, overflowY: "auto", mt: 2 }}>
										{templateVars.map((v, index) => (
											<Box
												key={v.id}
												display="flex"
												gap={1}
												mb={1}
												alignItems="center"
												p={1}
												bgcolor="#fff"
												border="1px solid #eee"
												borderRadius={1}
											>
												<Box
													sx={{
														minWidth: 40,
														bgcolor: "#e3f2fd",
														color: "#1976d2",
														p: 0.5,
														borderRadius: 1,
														textAlign: "center",
														fontWeight: "bold",
														fontSize: "0.85rem",
													}}
												>
													{`{{${index + 1}}}`}
												</Box>

												<TextField
													value={v.value}
													onChange={(e) => updateVariable(index, e.target.value)}
													size="small"
													fullWidth
													placeholder="Valor da variável"
													disabled={isSending}
													error={!v.value}
												/>

												<IconButton
													size="small"
													onClick={() => removeVariable(index)}
													disabled={isSending}
													color="error"
													title="Remover Variável"
												>
													<DeleteIcon fontSize="small" />
												</IconButton>
											</Box>
										))}
									</Box>
								</Box>
							</Box>
						</Grid>
					</Grid>

					{/* Envio */}
					<Box className="border p-4 rounded shadow-sm bg-white mb-4">
						<Typography variant="h6" gutterBottom color="primary">
							4. Envio
						</Typography>

						<Box mt={2}>
							<Typography variant="body1" fontWeight="bold">
								Status: {status}
							</Typography>
							{loading && <LinearProgress sx={{ mt: 1 }} />}
						</Box>

						<Box mt={3}>
							<Button
								variant="contained"
								color="primary"
								onClick={sendMessage}
								disabled={isSending || loading}
								size="large"
							>
								{isSending ? "Enviando..." : "Enviar"}
							</Button>
						</Box>
					</Box>
						</>
					)}
				</Box>
			</div>
			{/* Componente de Upload (Invisível/Modal) controlado por evento */}
			<UploadPhotoV2Image
				ident={"upload-whatsapp"}
				nameImg={"header.png"}
				id={"header"}
				onChange={(data) => {
					// O componente retorna { url: ... }
					if (data && data.url) {
						// Remove timestamp se vier e seta preview
						setImagePreview(data.url);
						toast.success("Imagem carregada!");
					}
				}}
				type={"card"}
			/>
			<Footer />
		</>
	);
};

export default WhatsAppSenderOfficial;
